import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import iconPresentActive from "@/assets/attendance/anwesend_aktiv.png";
import iconPresentInactive from "@/assets/attendance/anwesend_inaktiv.png";
import iconAbsentActive from "@/assets/attendance/fehlt_aktiv.png";
import iconAbsentInactive from "@/assets/attendance/fehlt_inaktiv.png";
import iconExcusedActive from "@/assets/attendance/entschuldigt_aktiv.png";
import iconExcusedInactive from "@/assets/attendance/entschuldigt_inaktiv.png";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function AttendanceTabModern({
  children,
  groups,
  sessions,
  attendance,
  setAttendance,
  selectedSessionId,
  setSelectedSessionId,
  authToken,
  authHeaders,
  toast,
  isMobile,
  isSuperAdmin,
  isAdmin,
  isTrainer,
  currentUser,
  formatDateISOToGerman,
  fetchAttendanceForSession,
  generateOfflinePDF,
  groupsLoading = false,
}) {
  const [groupFilter, setGroupFilter] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Nur aktive Sessions für die Anwesenheit (deaktivierte überspringen)
  const activeSessions = useMemo(() => {
    return (sessions || []).filter(s => s.active !== false);
  }, [sessions]);

  // Sortierte Sessions (neueste zuerst) - nur aktive
  const sortedSessions = useMemo(() => {
    return [...activeSessions].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [activeSessions]);

  // Aktueller Session-Index
  const currentSessionIndex = useMemo(() => {
    if (!selectedSessionId) return 0;
    const idx = sortedSessions.findIndex((s) => s.id === selectedSessionId);
    return idx >= 0 ? idx : 0;
  }, [sortedSessions, selectedSessionId]);

  const selectedSession = sortedSessions[currentSessionIndex];

  // Auto-select: nächsten kommenden aktiven Termin wählen
  useEffect(() => {
    if (sortedSessions.length === 0) return;
    // Wenn kein Termin gewählt oder der gewählte nicht in den aktiven Sessions ist
    const isSelectedActive = sortedSessions.some((s) => s.id === selectedSessionId);
    if (!selectedSessionId || !isSelectedActive) {
      const todayISO = new Date().toISOString().slice(0, 10);
      const ascending = [...sortedSessions].reverse();
      const nextFuture = ascending.find((s) => s.date >= todayISO);
      setSelectedSessionId(nextFuture ? nextFuture.id : sortedSessions[0].id);
    }
  }, [sortedSessions, selectedSessionId, setSelectedSessionId]);

  // Fetch attendance when group or session changes
  useEffect(() => {
    if (selectedSessionId && groupFilter && groupFilter !== "all") {
      fetchAttendanceForSession(selectedSessionId, groupFilter);
    }
  }, [selectedSessionId, groupFilter, fetchAttendanceForSession]);

  // Navigation zwischen Sessions
  const goToPrevSession = () => {
    const newIndex = Math.min(currentSessionIndex + 1, sortedSessions.length - 1);
    if (sortedSessions[newIndex]) {
      setSelectedSessionId(sortedSessions[newIndex].id);
    }
  };

  const goToNextSession = () => {
    const newIndex = Math.max(currentSessionIndex - 1, 0);
    if (sortedSessions[newIndex]) {
      setSelectedSessionId(sortedSessions[newIndex].id);
    }
  };

  // Prüfen ob heutiger Tag
  const isToday = useMemo(() => {
    if (!selectedSession) return false;
    const today = new Date().toISOString().split("T")[0];
    return selectedSession.date === today;
  }, [selectedSession]);

  // Berechtigungen
  const canEdit = isSuperAdmin || isAdmin || isTrainer || (currentUser?.role === "mitarbeiter" && isToday);

  // Geburtstags-Check: Prüft ob Kind heute oder in den letzten X Tagen Geburtstag hatte
  const getBirthdayInfo = (birthDateStr) => {
    if (!birthDateStr || !selectedSession?.date) return null;
    
    try {
      const sessionDate = new Date(selectedSession.date);
      const [birthYear, birthMonth, birthDay] = birthDateStr.split("-").map(Number);
      
      // Geburtstag in diesem Jahr (am Session-Datum)
      const birthdayThisYear = new Date(sessionDate.getFullYear(), birthMonth - 1, birthDay);
      
      // Differenz in Tagen zwischen Session und Geburtstag
      const diffTime = sessionDate.getTime() - birthdayThisYear.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // Neues Alter berechnen
      let newAge = sessionDate.getFullYear() - birthYear;
      if (diffDays < 0) {
        // Geburtstag ist noch nicht gewesen dieses Jahr
        newAge -= 1;
      }
      
      // Anzeigen wenn: heute (0), oder in den letzten 7 Tagen (1-7)
      if (diffDays >= 0 && diffDays <= 7) {
        return {
          daysAgo: diffDays,
          newAge: newAge,
          isToday: diffDays === 0
        };
      }
      
      return null;
    } catch (e) {
      return null;
    }
  };

  // Gruppen-Style
  const getGroupStyle = (groupId) => {
    const group = groups?.find((g) => g.id === groupId);
    if (!group) return { bg: "#e5e7eb", text: "#1f2937", fontSize: "var(--group-badge-font-size)", fontWeight: "var(--group-badge-font-weight)" };
    const bg = group.color || "#e5e7eb";
    let textColor = "#1f2937";
    
    // Prüfen ob manuelle Textfarbe definiert ist
    const presets = window.__groupColorPresets || [];
    const badgeSettings = window.__groupBadgeSettings || { text_color_mode: "auto" };
    const preset = presets.find(p => p.hex === bg);
    
    if (badgeSettings.text_color_mode === "manual" && preset?.text_color) {
      textColor = preset.text_color;
    } else {
      // Automatische Berechnung
      try {
        if (bg.startsWith("#") && bg.length === 7) {
          const r = parseInt(bg.slice(1, 3), 16);
          const g = parseInt(bg.slice(3, 5), 16);
          const b = parseInt(bg.slice(5, 7), 16);
          textColor = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#1f2937" : "#ffffff";
        }
      } catch (e) {}
    }
    
    return { 
      bg, 
      text: textColor,
      fontSize: "var(--group-badge-font-size)",
      fontWeight: "var(--group-badge-font-weight)"
    };
  };

  // Gefilterte Kinder für Anwesenheit
  const attendanceChildren = useMemo(() => {
    let list = (children || []).filter((c) => c.status !== "archiviert");
    if (groupFilter && groupFilter !== "all") {
      list = list.filter((c) => c.group_id === groupFilter);
    }
    return list.sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  }, [children, groupFilter]);

  // Anwesenheitszählung pro Gruppe
  const getGroupAttendanceCounts = (groupId) => {
    const groupChildren = (children || []).filter((c) => c.group_id === groupId && c.status !== "archiviert");
    const present = groupChildren.filter((c) => attendance[c.id]?.status === "anwesend" || attendance[c.id]?.status === "present").length;
    return { present, total: groupChildren.length };
  };

  // Gesamtzählung ALLER Kinder für den Tag (für Bäder-Betriebe)
  const dailyTotalCounts = useMemo(() => {
    const allActiveChildren = (children || []).filter((c) => c.status !== "archiviert");
    const present = allActiveChildren.filter((c) => ["anwesend", "present"].includes(attendance[c.id]?.status)).length;
    const absent = allActiveChildren.filter((c) => ["fehlt", "absent"].includes(attendance[c.id]?.status)).length;
    const excused = allActiveChildren.filter((c) => ["entschuldigt", "excused"].includes(attendance[c.id]?.status)).length;
    return { present, absent, excused, total: allActiveChildren.length };
  }, [children, attendance]);

  // Gesamtzählung (für aktuelle Gruppenansicht)
  const totalCounts = useMemo(() => {
    const list = groupFilter && groupFilter !== "all" 
      ? attendanceChildren 
      : (children || []).filter((c) => c.status !== "archiviert");
    const present = list.filter((c) => ["anwesend", "present"].includes(attendance[c.id]?.status)).length;
    const absent = list.filter((c) => ["fehlt", "absent"].includes(attendance[c.id]?.status)).length;
    const excused = list.filter((c) => ["entschuldigt", "excused"].includes(attendance[c.id]?.status)).length;
    return { present, absent, excused, total: list.length };
  }, [children, attendanceChildren, attendance, groupFilter]);

  // Status Mapping (Frontend -> Backend)
  const statusToBackend = (status) => {
    const map = { present: "anwesend", absent: "fehlt", excused: "entschuldigt" };
    return map[status] || status;
  };

  // Anwesenheit umschalten
  const toggleAttendance = async (childId, newStatus) => {
    if (!canEdit) {
      toast({ title: "Keine Berechtigung", description: "Du kannst die Anwesenheit nicht bearbeiten.", variant: "destructive" });
      return;
    }
    if (!groupFilter || groupFilter === "all") {
      toast({ title: "Bitte zuerst eine konkrete Gruppe auswählen.", variant: "destructive" });
      return;
    }
    if (!selectedSessionId) {
      toast({ title: "Bitte zuerst einen Kurstermin auswählen.", variant: "destructive" });
      return;
    }

    const current = attendance[childId] || {};
    const finalStatus = current.status === newStatus ? null : newStatus;

    // Optimistisches Update
    setAttendance((prev) => ({
      ...prev,
      [childId]: finalStatus ? { ...prev[childId], status: finalStatus } : undefined,
    }));

    try {
      await axios.post(
        `${API_BASE}/api/attendance/bulk`,
        {
          session_id: selectedSessionId,
          group_id: groupFilter,
          entries: [{ child_id: childId, status: statusToBackend(finalStatus) || "fehlt", comment: current.comment || null }],
        },
        authHeaders()
      );
    } catch (e) {
      // Bei Fehler zurücksetzen
      setAttendance((prev) => ({
        ...prev,
        [childId]: current,
      }));
      toast({ title: "Fehler beim Speichern", variant: "destructive" });
    }
  };

  // Alle als anwesend markieren
  const markAllPresent = async () => {
    if (!canEdit || !groupFilter || groupFilter === "all" || !selectedSessionId) return;
    
    setIsSaving(true);
    const entries = attendanceChildren.map((c) => ({
      child_id: c.id,
      status: "anwesend",
      comment: attendance[c.id]?.comment || null,
    }));

    try {
      await axios.post(
        `${API_BASE}/api/attendance/bulk`,
        { session_id: selectedSessionId, group_id: groupFilter, entries },
        authHeaders()
      );
      await fetchAttendanceForSession(selectedSessionId, groupFilter);
      toast({ title: "Alle als anwesend markiert" });
    } catch (e) {
      toast({ title: "Fehler beim Speichern", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Session-Navigator */}
      {sortedSessions.length > 0 && (
        <div className="flex items-center justify-between rounded-xl bg-white p-3 shadow-sm">
          <button
            onClick={goToPrevSession}
            disabled={currentSessionIndex >= sortedSessions.length - 1}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="text-center">
            <div className="text-lg font-semibold text-slate-900">
              {selectedSession ? formatDateISOToGerman(selectedSession.date) : "–"}
            </div>
            <div className="text-sm text-slate-500">
              {selectedSession?.weekday || ""}
              {isToday && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Heute</span>}
            </div>
            {/* Tages-Teilnehmerzahl für Bäder-Betriebe */}
            <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="text-sm font-bold text-emerald-700">{dailyTotalCounts.present}</span>
              <span className="text-xs text-slate-500">Teilnehmer</span>
            </div>
          </div>
          <button
            onClick={goToNextSession}
            disabled={currentSessionIndex <= 0}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Gruppen-Filter */}
      <div className="flex flex-wrap gap-2">
        {groupsLoading ? (
          <div className="flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-slate-600">
            <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm">Gruppen werden geladen...</span>
          </div>
        ) : groups?.length === 0 ? (
          <div className="rounded-full bg-amber-100 px-4 py-2 text-sm text-amber-700">
            Keine Gruppen verfügbar
          </div>
        ) : (
          groups?.map((g) => {
            const style = getGroupStyle(g.id);
            const counts = getGroupAttendanceCounts(g.id);
            const isSelected = groupFilter === g.id;
            return (
              <button
                key={g.id}
                onClick={() => setGroupFilter(isSelected ? null : g.id)}
                className={`rounded-full px-4 py-2 transition-all ${isSelected ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                style={{ 
                  backgroundColor: style.bg, 
                  color: style.text,
                  fontSize: style.fontSize,
                  fontWeight: style.fontWeight
                }}
              >
                {g.name} · {g.start_time?.slice(0, 5)} ({counts.present}/{counts.total})
              </button>
            );
          })
        )}
      </div>

      {/* Hinweis wenn keine Gruppe ausgewählt */}
      {!groupFilter && (
        <div className="rounded-xl bg-amber-50 p-4 text-center">
          <p className="text-sm text-amber-700">Bitte wähle eine Gruppe aus, um die Anwesenheit zu erfassen.</p>
        </div>
      )}

      {/* Umkleidekabinen-Anzeige wenn Gruppe ausgewählt */}
      {groupFilter && (() => {
        const selectedGroup = groups?.find(g => g.id === groupFilter);
        const hasBoys = selectedGroup?.changing_room_boys;
        const hasGirls = selectedGroup?.changing_room_girls;
        if (!hasBoys && !hasGirls) return null;
        
        return (
          <div className="rounded-xl bg-gradient-to-r from-blue-50 to-pink-50 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
              </svg>
              <span className="text-sm font-semibold text-slate-700">Umkleidekabinen</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {hasBoys && (
                <div className="flex items-center gap-3 rounded-lg bg-white/80 p-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500 text-white font-bold text-lg">
                    J
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Jungen</div>
                    <div className="text-sm font-semibold text-slate-900">{selectedGroup.changing_room_boys}</div>
                  </div>
                </div>
              )}
              {hasGirls && (
                <div className="flex items-center gap-3 rounded-lg bg-white/80 p-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-pink-500 text-white font-bold text-lg">
                    M
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Mädchen</div>
                    <div className="text-sm font-semibold text-slate-900">{selectedGroup.changing_room_girls}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Anwesenheitsliste */}
      {groupFilter && (
        <>
          {/* Statistik-Leiste */}
          <div className="flex items-center justify-between rounded-xl bg-white p-3 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <img src={iconPresentActive} alt="" className="h-5 w-5" />
                <span className="text-sm font-medium text-emerald-700">{totalCounts.present}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <img src={iconAbsentActive} alt="" className="h-5 w-5" />
                <span className="text-sm font-medium text-rose-700">{totalCounts.absent}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <img src={iconExcusedActive} alt="" className="h-5 w-5" />
                <span className="text-sm font-medium text-amber-700">{totalCounts.excused}</span>
              </div>
              <span className="text-sm text-slate-400">/ {totalCounts.total}</span>
            </div>
            <div className="flex gap-2">
              {canEdit && (
                <button
                  onClick={markAllPresent}
                  disabled={isSaving}
                  className="rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-200 disabled:opacity-50"
                >
                  Alle anwesend
                </button>
              )}
              {generateOfflinePDF && (
                <button
                  onClick={() => generateOfflinePDF(groupFilter)}
                  className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200"
                >
                  PDF
                </button>
              )}
            </div>
          </div>

          {/* Mitarbeiter-Hinweis */}
          {currentUser?.role === "mitarbeiter" && !isToday && (
            <div className="rounded-xl bg-amber-50 p-3 text-center">
              <p className="text-xs text-amber-700">Als Mitarbeiter kannst du Anwesenheiten nur für den aktuellen Kurstag bearbeiten.</p>
            </div>
          )}

          {/* Kinder-Liste */}
          <div className="space-y-2">
            {attendanceChildren.length === 0 ? (
              <div className="rounded-xl bg-white p-6 text-center shadow-sm">
                <p className="text-sm text-slate-500">Keine Kinder in dieser Gruppe.</p>
              </div>
            ) : (
              attendanceChildren.map((child) => {
                const currentStatus = attendance[child.id]?.status || null;
                // Normalize status for display (both German and English values)
                const isPresent = ["anwesend", "present"].includes(currentStatus);
                const isAbsent = ["fehlt", "absent"].includes(currentStatus);
                const isExcused = ["entschuldigt", "excused"].includes(currentStatus);
                const groupStyle = getGroupStyle(child.group_id);
                const initials = `${child.first_name?.[0] || ""}${child.last_name?.[0] || ""}`;
                const hasComment = !!attendance[child.id]?.comment;
                const birthdayInfo = getBirthdayInfo(child.birth_date);

                return (
                  <div
                    key={child.id}
                    className={`flex items-center justify-between rounded-xl bg-white p-3 shadow-sm transition-all ${
                      isPresent ? "ring-1 ring-emerald-200" : 
                      isAbsent ? "ring-1 ring-rose-200" : 
                      isExcused ? "ring-1 ring-amber-200" : ""
                    } ${birthdayInfo ? "ring-2 ring-pink-300 bg-gradient-to-r from-pink-50 to-white" : ""}`}
                  >
                    <div className="flex items-center gap-3">
                      {child.has_photo ? (
                        <img
                          src={child.photo_thumb_url || `${API_BASE}/api/children/${child.id}/photo?token=${authToken}&size=thumb`}
                          alt=""
                          className="h-10 w-10 rounded-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="flex h-10 w-10 items-center justify-center rounded-full text-xs font-semibold"
                          style={{ backgroundColor: groupStyle.bg, color: groupStyle.text }}
                        >
                          {initials}
                        </div>
                      )}
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span style={{ fontSize: 'var(--font-names-size)', fontWeight: 'var(--font-names-weight)' }} className="text-slate-900">{child.first_name} {child.last_name}</span>
                          {birthdayInfo && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-pink-100 px-2 py-0.5 text-xs font-medium text-pink-700">
                              🎂 {birthdayInfo.newAge} Jahre
                              {birthdayInfo.isToday ? " heute!" : birthdayInfo.daysAgo === 1 ? " (gestern)" : ` (vor ${birthdayInfo.daysAgo} Tagen)`}
                            </span>
                          )}
                          {child.membership_end_date && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">
                              ⚠ Kündigung: {formatDateISOToGerman(child.membership_end_date)}
                            </span>
                          )}
                        </div>
                        {hasComment && (
                          <div className="text-xs text-slate-500 truncate max-w-[150px]">
                            💬 {attendance[child.id].comment}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {[
                        { status: "present", isActive: isPresent, activeIcon: iconPresentActive, inactiveIcon: iconPresentInactive, ring: "ring-emerald-400", bg: "bg-emerald-50" },
                        { status: "absent", isActive: isAbsent, activeIcon: iconAbsentActive, inactiveIcon: iconAbsentInactive, ring: "ring-rose-400", bg: "bg-rose-50" },
                        { status: "excused", isActive: isExcused, activeIcon: iconExcusedActive, inactiveIcon: iconExcusedInactive, ring: "ring-amber-400", bg: "bg-amber-50" },
                      ].map(({ status, isActive, activeIcon, inactiveIcon, ring, bg }) => (
                        <button
                          key={status}
                          onClick={() => toggleAttendance(child.id, status)}
                          disabled={!canEdit}
                          className={`flex h-11 w-11 items-center justify-center rounded-xl transition-all ${
                            isActive ? `${bg} ring-2 ${ring}` : "hover:bg-slate-50"
                          } ${!canEdit ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          <img
                            src={isActive ? activeIcon : inactiveIcon}
                            alt=""
                            className="h-7 w-7"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
